import {
  MutableRefObject,
  useCallback,
  useEffect,
} from 'react';

interface UseProgrammaticFocusOptions {
  querySelector?: string
}

/**
 * Hook to help programmatically focus a non-interactive element.
 * To ensure that an element becomes focusable, we add a -1 to the tabindex,
 * and remove it once blurred.
 */
export default function useProgrammaticFocus<T extends HTMLElement>(
  refOrElement: T | MutableRefObject<T>,
  options: UseProgrammaticFocusOptions = {},
) {
  const resolveElement = useCallback(() => {
    let element: T = null;

    if (!refOrElement) {
      return element;
    }

    if (typeof refOrElement === 'object' && 'current' in refOrElement) {
      element = refOrElement.current;
    } else {
      element = refOrElement;
    }

    if (element && options.querySelector) {
      element = element.querySelector(options.querySelector);
    }

    return element;
  }, [options.querySelector, refOrElement]);


  useEffect(() => {
    const element = resolveElement();

    if (!element) return;

    function onBlur() {
      element.removeAttribute('tabindex');
    }

    element.addEventListener('blur', onBlur);

    // eslint-disable-next-line consistent-return
    return () => {
      element.removeEventListener('blur', onBlur);
    };
  }, [resolveElement]);

  function isFocusableElement(element: HTMLElement) {
    const focusableElementTags = [
      'a[href]',
      'button',
      'input',
      'textarea',
      'select',
      'details',
      '[tabindex]:not([tabindex="-1"])',
    ];
    return focusableElementTags.some((selector) => element.matches(selector));
  }

  const focus = useCallback(() => {
    const element = resolveElement();
    if (!element) {
      return;
    }
    if (!isFocusableElement(element)) {
      element.setAttribute('tabindex', '-1');
    }
    element.focus();
  }, [resolveElement]);

  return focus;
}
